import type { PublicActionItem } from '@/backend/contracts/action-items-client.ts';

import type { ActionItemCloseReason, ActionItemReasonLabel } from './types.ts';

export type ActionItemClearablePatchField =
  | 'description'
  | 'dueDate'
  | 'priority'
  | 'department'
  | 'mergedIntoId';

export type ActionItemPatch = Partial<Pick<
  PublicActionItem,
  | 'title'
  | 'reasonLabels'
  | 'description'
  | 'dueDate'
  | 'priority'
  | 'department'
  | 'lifecycle'
  | 'closeReason'
  | 'mergedIntoId'
>>;

const OPTIONAL_STRING_PATCH_FIELDS = [
  'description',
  'dueDate',
  'department',
  'mergedIntoId',
] as const;

function canonicalizePatchTitle(patch: ActionItemPatch): void {
  if (!Object.hasOwn(patch, 'title') || typeof patch.title !== 'string') return;
  patch.title = patch.title.trim() || 'Untitled action item';
}

function canonicalizeOptionalStringPatch(
  patch: ActionItemPatch,
  clearFields: Set<ActionItemClearablePatchField>,
  field: typeof OPTIONAL_STRING_PATCH_FIELDS[number],
): void {
  if (!Object.hasOwn(patch, field)) return;
  const value = patch[field];
  if (value !== undefined && (typeof value !== 'string' || value.trim())) {
    clearFields.delete(field);
    return;
  }
  delete patch[field];
  clearFields.add(field);
}

function canonicalizePriorityPatch(
  patch: ActionItemPatch,
  clearFields: Set<ActionItemClearablePatchField>,
): void {
  if (!Object.hasOwn(patch, 'priority')) return;
  if (patch.priority !== undefined) {
    clearFields.delete('priority');
    return;
  }
  delete patch.priority;
  clearFields.add('priority');
}

export function canonicalizeActionItemPatch(
  input: ActionItemPatch,
  explicitClearFields: readonly ActionItemClearablePatchField[] = [],
): { patch: ActionItemPatch; clearFields: ActionItemClearablePatchField[] } {
  const patch = { ...input };
  const clearFields = new Set(explicitClearFields);

  for (const field of explicitClearFields) delete patch[field];
  canonicalizePatchTitle(patch);
  for (const field of OPTIONAL_STRING_PATCH_FIELDS)
    canonicalizeOptionalStringPatch(patch, clearFields, field);
  canonicalizePriorityPatch(patch, clearFields);

  return { patch, clearFields: [...clearFields] };
}

export interface CreateActionItemInput {
  title: string;
  description?: string;
  reasonLabels: readonly ActionItemReasonLabel[];
  priority?: PublicActionItem['priority'];
  dueDate?: string;
  department?: string;
  nowIso?: string;
  actorId?: string;
  id?: string;
}

export function newActionItemId(): string {
  return crypto.randomUUID();
}

function timestamp(nowIso?: string): string {
  return nowIso ?? new Date().toISOString();
}

function copyPublicActionItem(item: PublicActionItem): PublicActionItem {
  return {
    id: item.id,
    title: item.title,
    lifecycle: item.lifecycle,
    reasonLabels: [...item.reasonLabels],
    createdAt: item.createdAt,
    ...(item.createdBy ? { createdBy: item.createdBy } : {}),
    updatedAt: item.updatedAt,
    ...(item.updatedBy ? { updatedBy: item.updatedBy } : {}),
    provenance: { kind: item.provenance.kind },
    ...(item.description ? { description: item.description } : {}),
    ...(item.priority ? { priority: item.priority } : {}),
    ...(item.dueDate ? { dueDate: item.dueDate } : {}),
    ...(item.department ? { department: item.department } : {}),
    ...(item.closeReason ? { closeReason: item.closeReason } : {}),
    ...(item.mergedIntoId ? { mergedIntoId: item.mergedIntoId } : {}),
    ...(item.sanitizedProvenanceSummary ? { sanitizedProvenanceSummary: item.sanitizedProvenanceSummary } : {}),
  };
}

export function createActionItem(input: CreateActionItemInput): PublicActionItem {
  const now = timestamp(input.nowIso);
  return {
    id: input.id ?? newActionItemId(),
    title: input.title.trim() || 'Untitled action item',
    ...(input.description?.trim() ? { description: input.description.trim() } : {}),
    lifecycle: 'active',
    reasonLabels: [...input.reasonLabels],
    ...(input.priority ? { priority: input.priority } : {}),
    ...(input.dueDate ? { dueDate: input.dueDate } : {}),
    ...(input.department ? { department: input.department } : {}),
    createdAt: now,
    updatedAt: now,
    ...(input.actorId ? { createdBy: input.actorId, updatedBy: input.actorId } : {}),
    provenance: { kind: 'manual' },
  };
}

export function closeActionItem(
  item: PublicActionItem,
  input: {
    closeReason: ActionItemCloseReason;
    nowIso?: string;
    actorId?: string;
  },
): PublicActionItem {
  if (item.lifecycle === 'closed') {
    throw new Error('Only open action items can be closed');
  }

  const now = timestamp(input.nowIso);
  return {
    ...copyPublicActionItem(item),
    lifecycle: 'closed',
    closeReason: input.closeReason,
    updatedAt: now,
    ...(input.actorId ? { updatedBy: input.actorId } : {}),
  };
}

export function reopenActionItem(
  item: PublicActionItem,
  input: {
    nowIso?: string;
    actorId?: string;
  },
): PublicActionItem {
  if (item.lifecycle !== 'closed') {
    throw new Error('Only closed action items can be reopened');
  }

  const now = timestamp(input.nowIso);
  return {
    ...copyPublicActionItem(item),
    lifecycle: 'active',
    closeReason: undefined,
    updatedAt: now,
    ...(input.actorId ? { updatedBy: input.actorId } : {}),
  };
}

export function isOpenActionItem(item: PublicActionItem): boolean {
  return item.lifecycle !== 'closed';
}
